import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SectionGlow from '@/components/common/SectionGlow';
import { TileImage } from '@/components/common/Skeleton';
import axios from 'axios';
import { shopTypeLabel } from '@/lib/shopType';
import { CoinPrice } from '@/components/common/CoinPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Product suggestion (approved by SAMTWO, not spec-driven): a small strip surfacing
// purchasable content to non-subscribers for discovery/conversion. Auto-populates
// from GET /api/shop; hides entirely when the catalog has nothing purchasable.
const HomeShopStrip = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState(undefined); // undefined=loading

  useEffect(() => {
    axios.get(`${API}/shop`).then((r) => {
      const d = r.data || {};
      const merged = [...(d.albums_eps || []), ...(d.singles || []), ...(d.videos || [])].slice(0, 4);
      setItems(merged);
    }).catch(() => setItems([]));
  }, []);

  if (items === undefined || items.length === 0) return null;

  return (
    <section data-testid="home-shop-strip" className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="85% 15%" strength={9} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-overline">{t('home.shop.overline')}</span>
            <h2 className="text-h2 font-bold mt-2" style={{ color: 'var(--text-primary)' }}>{t('home.shop.title')}</h2>
          </div>
          <Link to="/shop" data-testid="home-shop-strip-viewall" className="text-sm font-semibold inline-flex items-center gap-1 whitespace-nowrap" style={{ color: 'var(--accent-primary)' }}>
            {t('home.shop.view')}
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((item, i) => (
            <motion.div key={`${item.content_type}-${item.content_id}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
              <Link to={`/shop?item=${item.content_type}:${item.content_id}`} data-testid={`home-shop-card-${item.content_id}`} className="block rounded-2xl overflow-hidden group" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                <div style={{ aspectRatio: item.content_type === 'video' ? '16 / 9' : '1 / 1', background: 'var(--bg-subtle)', overflow: 'hidden', position: 'relative' }}>
                  {item.cover && <TileImage src={item.cover} alt={item.title} extraTransition="transform 500ms" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{shopTypeLabel(item.type_code, t)}</span>
                    <span className="inline-flex items-baseline gap-1.5">
                      {item.compare_at_cents != null && <CoinPrice cents={item.compare_at_cents} size="sm" strike />}
                      <CoinPrice cents={item.viewer_price_cents ?? item.price_full_cents} size="sm" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeShopStrip;
